// @ts-strict-ignore
import {
  isEventBasedEntry,
  isFormulaEntry,
  MetricEntry,
  MetricEntryBehavior,
} from 'common/query-builder/query-entry-section/types';
import {MetricBehavior, MetricMeasurement} from 'common/report/insights/util/types';
import {BehaviorShowClause, ShowClause} from 'common/report/insights/models/show-clause';
import {ShowSection} from 'common/report/insights/models/section';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {
  MetricType,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  TopLevelMetricType,
} from 'common/types/reports/bookmark';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {
  convertPropertySelectiontoBookmarkProperty,
  createRetentionMetricShowClause,
  getMetricEntryBehavior,
  isSavedBehavior,
  isSavedMetric,
} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';
import {MetricsResource} from 'common/report/model-resources/metrics';

import {isFormulaShowClause} from '../models/utils';
import {ReportSectionsMetadata, ShowClause as ShowClauseType} from '../util/types';
import {FilterClause} from '../models/filter-clause';
import {BehaviorMetric, InsightsQueryError} from './types';
import {createFunnelMetricShowClause, getMetricEntryMeasurement} from './events';

/**
 * Transforms clauses in a bookmark to conform to the MetricEntry type
 * The Insights QB is based only on this type of project as of the
 * First Class Metrics project
 */
export function getMetricEntriesFromShowClauses({
  showClauses,
  dateUnit,
  reportSectionsMetadata,
}: {
  showClauses: ShowClauseType[];
  behaviorsResource?: BehaviorsResource;
  metricsResource?: MetricsResource;
  dataGroups?: DataGroup[];
  queryError?: InsightsQueryError;
  dateUnit?: TimeUnit;
  reportSectionsMetadata?: ReportSectionsMetadata;
}): MetricEntry[] {
  const entries: MetricEntry[] = [];

  showClauses.forEach((clause: ShowClauseType, index: number) => {
    /** @todo handle formulas */
    if (isFormulaShowClause(clause)) {
      return;
    }

    const props: MetricEntry = {
      type: TopLevelMetricType.NewMetricEntry,
      id: clause.id,
      name: clause.name,
      behavior: getMetricEntryBehavior(clause.behavior),
      measurement: getMetricEntryMeasurement({
        clause: clause as BehaviorMetric,
        index,
        dateUnit,
        reportSectionsMetadata,
      }),
      display: clause.display,
    };

    entries.push(props);
  });

  return entries;
}

/**
 * Transforms metric entries to show clauses within a show section
 */
export function getShowSectionFromMetricsEntries({
  entries,
  dataGroupId,
  attributionSelection,
  queryType,
}: {
  entries: MetricEntry[];
  dataGroupId?: string;
  attributionSelection?: number | BehaviorShowClause[`measurement`][`actionMode`];
  queryType?: MetricType;
}): ShowSection {
  if (entries.length === 0) {
    return ShowSection.createShowSection({
      behavior: {
        dataGroupId,
        type: MetricType.Event,
        // Cleanup: Missing resourceType
        resourceType: undefined,
      },
    });
  }

  const showClauses = entries.map((entry) => {
    /** @todo handle formulas */
    if (isFormulaEntry(entry)) {
      return null;
    }

    let metricBehavior: MetricBehavior | null;
    let metricMeasurement: MetricMeasurement | null;

    /** @todo (pablo) this whole block of code will be simplified/updated to work with metrics only */
    if (entry.behavior.type === MetricType.Funnel) {
      const clause = createFunnelMetricShowClause(entry, attributionSelection) as BehaviorShowClause;
      metricBehavior = clause.behavior;
      metricMeasurement = clause.measurement;
    } else if (entry.behavior.type === MetricType.Retention) {
      const metricType = queryType ?? MetricType.Retention;
      const clause = createRetentionMetricShowClause(metricType, entry, attributionSelection) as BehaviorShowClause;
      metricBehavior = clause.behavior;
      metricMeasurement = clause.measurement;
    } else if (entry.behavior.type === MetricType.RetentionFrequency) {
      const metricType = queryType ?? MetricType.RetentionFrequency;
      const clause = createRetentionMetricShowClause(metricType, entry, attributionSelection) as BehaviorShowClause;
      metricBehavior = clause.behavior;
      metricMeasurement = clause.measurement;
    } else {
      metricBehavior = transformMetricEntryToMetricBehavior(entry.behavior);
      metricMeasurement = {
        math: entry.measurement.aggregationOperator || PRIMITIVE_MATH_TYPES.TOTAL,
        perUserAggregation: (entry.measurement.aggregationOperatorPerUser as PER_USER_AGGREGATIONS) || null,
        rolling: entry.measurement.rolling || null,
        cumulative: entry.measurement.cumulative || false,
        segmentMethod: entry.measurement.countUsersOnceType,

        // converts a property aggregation to a valid bookmark format
        property: entry.measurement.property
          ? convertPropertySelectiontoBookmarkProperty(entry.measurement.property)
          : null,

        // attribution breakdowns only happen for event based metrics
        ...(isEventBasedEntry(entry) && entry.measurement.multiAttribution
          ? {multiAttribution: entry.measurement.multiAttribution}
          : {}),

        // dataGroupId goes in behavior for cohort / profile entries
        ...(isEventBasedEntry(entry) && (entry.dataGroupId ? {dataGroupId: entry.dataGroupId} : {})),
      };
    }

    // when dealing with a saved behavior, we only want to keep
    // the hydrated behavior fields in the bookmark in case
    // the user has modified the actual behavior definition
    const isUnmodifiedSavedBehavior = isSavedBehavior(entry) && !entry.hasUnsavedChanges;
    metricBehavior = {
      id: entry.behavior.id,
      type: entry.behavior.type,
      name: entry.behavior.name,
      hasUnsavedChanges: entry.hasUnsavedChanges ?? false,

      // persist all fields in bookmark for unsaved behaviors and behaviors with changes
      ...(!isUnmodifiedSavedBehavior ? metricBehavior : {}),
    };

    // when dealing with a saved metric, we only want to keep
    // the hydrated metric fields in the bookmark in case
    // the user has modified the actual metric definition
    const isUnmodifiedSavedMetric = isSavedMetric(entry) && !entry.behavior.hasUnsavedChanges;
    const metric: BehaviorMetric = {
      type: TopLevelMetricType.Metric,
      id: entry.id,
      name: entry.name,
      isHidden: entry.isHidden ?? false,
      isExpanded: entry.isExpanded ?? false,
      hasUnsavedChanges: entry.hasUnsavedChanges ?? false,

      // persist all fields in bookmark for unsaved metrics and metrics with changes
      ...(!isUnmodifiedSavedMetric
        ? {display: entry.display, behavior: metricBehavior, measurement: metricMeasurement}
        : {}),
    };

    return ShowClause.createShowClause(metric);
  });

  return new ShowSection(showClauses);
}

export function transformMetricEntryToMetricBehavior(behavior: MetricEntryBehavior): MetricBehavior {
  const subBehaviors = behavior.behaviors ?? [];
  const filters = behavior.filters ?? [];
  const serializeFilter = (filter) => FilterClause.fromPropertyFilter(filter).serialize();

  return {
    id: behavior.id,
    type: behavior.type,
    name: behavior.name,
    renamed: behavior.renamed,
    resourceType: behavior.resourceType,
    dataGroupId: behavior.dataGroupId,
    filters: filters.map(serializeFilter),
    customBucket: behavior.customBucket,
    behaviors: subBehaviors.map((subBehavior) => {
      return {
        id: subBehavior.id,
        type: subBehavior.type,
        name: subBehavior.name,
        filters: subBehavior.filters?.map(serializeFilter),
        filtersDeterminer: subBehavior.filtersDeterminer,
      };
    }),
  };
}
